import { useMutation } from '@tanstack/react-query'
import { equals } from 'ramda'
import { useMemo } from 'react'

import { generateEmbeddings } from '@/graphql/mutations'
import { getCustomComponent } from '@/graphql/queries'
import { SemanticSearchSettings } from '@/screens/ComponentLibrary/CustomComponents/SemanticSearch/components'
import {
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'
import { graphQLRequest } from '@/utils'
import {
  GenerateEmbeddingsMutation,
  GenerateEmbeddingsMutationVariables,
  GetCustomComponentQuery,
  GetCustomComponentQueryVariables,
} from '@/utils/api'

export const useGenerateEmbeddings = (componentID?: string) => {
  const updateSemanticSearch =
    useUpdateCustomComponent<SemanticSearchSettings>(componentID)
  const component = useGetCustomComponent<SemanticSearchSettings>(componentID)
  const normalizeComponentQuestions = useMemo(
    () =>
      (component.data?.settings.questions?.map((question) => ({
        suggestedQuestions: [],
        ...question,
      })) ?? []) as SemanticSearchSettings['questions'],
    [component.data]
  )

  return useMutation({
    mutationFn: async ({
      id,
      versionId,
    }: {
      id?: string | null
      versionId?: string | null
    }) => {
      const customComponentID = id ?? componentID
      if (!customComponentID) throw new Error('No document set ID provided')

      if (!versionId) throw new Error('No version  provided')
      if (
        !equals(normalizeComponentQuestions, component.data?.settings.questions)
      )
        await updateSemanticSearch.mutateAsync({
          id: customComponentID,
          ...component.data?.settings,
          //@ts-ignore
          questions: normalizeComponentQuestions,
        })

      const getPineconeIndexName = (env: string) =>
        env === 'secure' ? 'vail-q-a-prod' : 'vail-q-a-nonprod'

      const embedding = await graphQLRequest<
        GenerateEmbeddingsMutation,
        GenerateEmbeddingsMutationVariables
      >(generateEmbeddings, {
        customComponentID,
        pineconeIndexName: getPineconeIndexName(
          import.meta.env.VITE_APP_UPPER_ENV
        ),
        versionId,
      })

      await graphQLRequest<
        GetCustomComponentQuery,
        GetCustomComponentQueryVariables
      >(getCustomComponent, {
        id: customComponentID,
      })

      return embedding.data.generateEmbeddings
    },
  })
}
